import Autocomplete, { autocompleteClasses } from '@mui/material/Autocomplete';
import Avatar from '@mui/material/Avatar';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Popper from '@mui/material/Popper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { SxProps, styled, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import * as React from 'react';
import { ListChildComponentProps, VariableSizeList } from 'react-window';
import { IEmpresa } from '@/redux/models/empresaTypes';

const LISTBOX_PADDING = 1; // px

function renderRow(props: ListChildComponentProps) {
    const { data, index, style } = props;
    const dataSet = data[index];
    const itemData = dataSet[1] as IEmpresa;
    const inlineStyle = {
        ...style,
        top: (style.top as number) + LISTBOX_PADDING,
        height: (style.height as number),
    };


    return (
        <ListItem alignItems="flex-start" {...dataSet[0]} style={inlineStyle}>
            <ListItemAvatar>
                <Avatar alt={itemData.nombre} src={itemData.foto} />
            </ListItemAvatar>
            <ListItemText
                sx={{ py: 1 }}
                primary={itemData.nombre}
                secondary={
                    <React.Fragment>
                        <Typography
                            sx={{ display: 'inline' }}
                            component="span"
                            variant="body2"
                            color="text.primary"
                        >
                        </Typography>
                        {itemData.descripcion}
                    </React.Fragment>
                }
            />
        </ListItem>
    );
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef<HTMLDivElement>((props, ref) => {
    const outerProps = React.useContext(OuterElementContext);
    return <div ref={ref} {...props} {...outerProps} />;
});

function useResetCache(data: any) {
    const ref = React.useRef<VariableSizeList>(null);
    React.useEffect(() => {
        if (ref.current != null) {
            ref.current.resetAfterIndex(0, true);
        }
    }, [data]);
    return ref;
}

// Adapter for react-window
const ListboxComponent = React.forwardRef<
    HTMLDivElement,
    React.HTMLAttributes<HTMLElement>
>(function ListboxComponent(props, ref) {
    const { children, ...other } = props;
    const itemData: React.ReactElement[] = [];
    (children as React.ReactElement[]).forEach(
        (item: React.ReactElement & { children?: React.ReactElement[] }) => {
            itemData.push(item);
            itemData.push(...(item.children || []));
        },
    );

    const theme = useTheme();
    const smUp = useMediaQuery(theme.breakpoints.up('sm'));
    const itemCount = itemData.length;
    const itemSize = smUp ? 72 : 96;

    const getChildSize = (child: React.ReactElement) => {
        return itemSize;
    };

    const getHeight = () => {
        if (itemCount > 8) {
            return 8 * itemSize;
        }
        return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
    };

    const gridRef = useResetCache(itemCount);

    return (
        <div ref={ref}>
            <OuterElementContext.Provider value={other}>
                {/* @ts-ignore */}
                <VariableSizeList
                    itemData={itemData}
                    height={getHeight() + 2 * LISTBOX_PADDING}
                    width="100%"
                    ref={gridRef}
                    outerElementType={OuterElementType}
                    innerElementType="ul"
                    itemSize={(index) => getChildSize(itemData[index])}
                    overscanCount={5}
                    itemCount={itemCount}
                >
                    {renderRow}
                </VariableSizeList>
            </OuterElementContext.Provider>
        </div>
    );
});



const StyledPopper = styled(Popper)({
    [`& .${autocompleteClasses.listbox}`]: {
        boxSizing: 'border-box',
        '& ul': {
            padding: 0,
            margin: 0,
        },
    },
});


export default function AutocompleteEmpresas({ rows, loading, onChange, empresa, sx }: { rows: IEmpresa[], loading: boolean, empresa: IEmpresa | null, onChange: (empresa: IEmpresa | null) => void, sx?: SxProps<any> }) {

    return (
        <Autocomplete
            id="empresas-selector"
            sx={{ width: 300, float: 'right', ...sx }}
            disableListWrap
            loading={loading}
            loadingText="Cargando empresas..."
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            PopperComponent={StyledPopper}
            getOptionLabel={(option) => option.nombre}
            value={empresa || null}
            onChange={(_, newValue) => onChange(newValue)}
            ListboxComponent={ListboxComponent}
            options={rows}
            renderInput={(params) => <TextField {...params} label="Seleccioná una empresa" />}
            renderOption={(props, option, state) =>
                [props, option, state.index] as React.ReactNode
            }
            renderGroup={(params) => params as any}
        />
    );
}